import React, { useEffect } from 'react'

import { Box, Grid, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import { Barcode } from 'src/components/Barcode'

import { convertGS1 } from './utils'

const useStyles = makeStyles({
  label: {
    width: '4in',
    height: '6in',
  },
  zoneA: {
    borderRight: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '1.25in',
  },
  zoneB: {
    borderLeft: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '2.75in',
    fontSize: '14px',
  },
  zoneC: {
    borderTop: '1px solid black',
    borderRight: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '2.5in',
  },
  zoneD: {
    borderTop: '1px solid black',
    borderLeft: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '1.5in',
    fontSize: '10px',
  },
  zoneE: {
    borderTop: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '4in',
  },
  zoneF: {
    borderTop: '1px solid black',
    borderRight: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '2.5in',
  },
  zoneG: {
    borderTop: '1px solid black',
    borderLeft: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '1.5in',
  },
  zoneH: {
    borderTop: '1px solid black',
    height: '2in',
    width: '4in',
  },
  header: {
    fontSize: '12px',
    fontWeight: 'bold',
    paddingLeft: '3px',
  },
  address: {
    lineHeight: '1.0',
    paddingLeft: '10px',
  },
  from: {
    lineHeight: '1.0',
    fontSize: '10px',
    paddingLeft: '5px',
  },
  carrier: {
    fontWeight: 'bold',
    fontSize: '11px',
    paddingLeft: '5px',
  },
  to: {
    lineHeight: '1.0',
    fontWeight: 'bold',
    paddingLeft: '10px',
  },
  shipTo: {
    lineHeight: '1.0',
    fontSize: '12px',
    fontWeight: 'bold',
    paddingLeft: '6px',
  },
  container: {
    top: 'auto',
    bottom: 0,
  },
  center: {
    flexGrow: 1,
    textAlign: 'center',
  },
})

interface LowesLabelProps {
  zoneA: string[]
  zoneB: string[]
  zoneC: string
  zoneD: string[]
  zoneE: {
    po: string
    sku: string
    department_number: string //  eslint-disable-line camelcase
    department_name: string //  eslint-disable-line camelcase
  }
  zoneF: string
  zoneG: string[]
  zoneH: string
}

interface Props {
  data: LowesLabelProps
  onReadyToPrint?: () => void
}

export const LowesLabel = (props: Props) => {
  const { data, onReadyToPrint } = props
  const ssccBarcode = { barcode: convertGS1(data.zoneH, 2), type: 'gs1-128', displayName: '' }
  const shipToBarcode = { barcode: convertGS1(data.zoneC, 3), type: 'gs1-128', displayName: '' }
  const forBarcode = { barcode: convertGS1(data.zoneF, 2), type: 'gs1-128', displayName: '' }
  const classes = useStyles()

  useEffect(() => {
    onReadyToPrint?.()
  }, [onReadyToPrint])

  return (
    <div className={`always-page-break ${classes.label}`}>
      <div className="print-page">
        <Box className={classes.label}>
          <Grid container spacing={0}>
            <Grid item>
              <Box className={classes.zoneA}>
                <Typography className={classes.header}>FROM</Typography>
                {data.zoneA.map((line) => (
                  <Typography key={line} className={classes.from}>
                    {line}
                  </Typography>
                ))}
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneB}>
                <Typography className={classes.header}>TO</Typography>
                {data.zoneB.map((line) => (
                  <Typography key={line} className={classes.to}>
                    {line}
                  </Typography>
                ))}
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneC}>
                <Grid container>
                  <Grid item>
                    <Typography className={classes.header}>SHIP TO POST:</Typography>
                  </Grid>
                  <Grid item>
                    <Box display="flex" justifyContent="center" ml={4}>
                      <Typography variant="h6">{shipToBarcode.barcode}</Typography>
                    </Box>
                  </Grid>
                </Grid>
                <Box ml={6} mr={6}>
                  <Barcode barcode={shipToBarcode} scale={2} height={10} />
                </Box>
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneD}>
                <Typography className={classes.header}>CARRIER</Typography>
                <Typography className={classes.carrier}>{data.zoneD[0]}</Typography>
                <Typography className={classes.carrier}>PRO #: {data.zoneD[1]}</Typography>
                <Typography className={classes.carrier}>B/L #: {data.zoneD[2]}</Typography>
              </Box>
            </Grid>
            <Grid item>
              <Box
                className={classes.zoneE}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Box ml={5} mr={5}>
                  <Grid container>
                    <Grid item xs={6}>
                      <Typography>PO: {data.zoneE.po}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>sku: {data.zoneE.sku}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>DEPT #: {data.zoneE.department_number}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>Dept Name: {data.zoneE.department_name}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneF}>
                <Grid container>
                  <Grid item>
                    <Typography className={classes.header}>FOR</Typography>
                  </Grid>
                  <Grid item>
                    <Box display="flex" justifyContent="center" ml={14}>
                      <Typography variant="h6">{forBarcode.barcode}</Typography>
                    </Box>
                  </Grid>
                </Grid>
                <Box ml={5} mr={5}>
                  <Barcode barcode={forBarcode} scale={2} height={10} />
                </Box>
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneG}>
                <Grid container>
                  {data.zoneG.map((line) => (
                    <Grid item key={line} xs={12}>
                      <Typography variant="inherit" className={classes.shipTo}>
                        {line}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
            <Grid item>
              <Box className={`${classes.zoneH} ${classes.container}`}>
                <Typography className={classes.header}>SSCC-18</Typography>
                <Box display="flex" justifyContent="center">
                  <Typography variant="h4">{ssccBarcode.barcode}</Typography>
                </Box>
                <Box ml={5} mr={5}>
                  <Barcode barcode={ssccBarcode} scale={2} height={20} />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </div>
    </div>
  )
}
